import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

// @mui material components
import {
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Chip,
  IconButton,
  Grid,
  Autocomplete,
} from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { formatPhoneNumberForDisplay } from "utils/phoneUtils";
import { api } from "utils/api";
import { notifyError, notifySuccess } from "utils/appNotify";
import PropTypes from "prop-types";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Icon } from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import AddCustomerContactLinkDialog from "components/AddCustomerContactLinkDialog";
import ContactLinkedAccountsMobileCards from "layouts/contact-view/components/ContactLinkedAccountsMobileCards";
import {
  fetchClientzoneContact,
  fetchClientzoneContacts,
  updateClientzoneContact,
} from "utils/clientzonePortalApi";
import { fetchCustomerMergedContacts } from "utils/customerUtils";
import {
  applyPrimaryCommunicationPrefs,
  getPrimaryContactIncompleteMessage,
  getPrimaryContactRequiredFieldsMessage,
  isPrimaryContactComplete,
} from "utils/contactPrimaryUtils";

const contactFieldLabelSx = {
  display: "flex",
  flexDirection: { xs: "column", sm: "row" },
  gap: { xs: 0.25, sm: 1 },
  alignItems: { xs: "flex-start", sm: "baseline" },
};

function EditContactDialog({ open, onClose, contact, onSave, allContacts }) {
  const [editedContact, setEditedContact] = useState(contact);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showSelectPrimaryDialog, setShowSelectPrimaryDialog] = useState(false);
  const [pendingNewPrimary, setPendingNewPrimary] = useState(null);

  useEffect(() => {
    setEditedContact(contact);
    setError(null);
    setSaving(false);
    setShowSelectPrimaryDialog(false);
    setPendingNewPrimary(null);
  }, [contact]);

  const otherContacts = useMemo(
    () =>
      (allContacts || []).filter(
        (row) => Number(row?.customer_contact_id) !== Number(editedContact?.customer_contact_id)
      ),
    [allContacts, editedContact?.customer_contact_id]
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setEditedContact({ ...editedContact, [field]: value });
  };

  const handleNotificationChange = (field) => (event) => {
    const newValue = event.target.checked ? "yes" : "no";

    if (newValue === "no") {
      const hasOtherYes = otherContacts.some((row) => row[field] === "yes");
      if (!hasOtherYes) {
        setError(
          `Cannot set ${field.replace(
            "get_",
            ""
          )} to No. At least one contact must receive ${field.replace("get_", "")}.`
        );
        return;
      }
    }

    setEditedContact({ ...editedContact, [field]: newValue });
    setError(null);
  };

  const handlePrimaryChange = (event) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      const existingPrimary = otherContacts.find((row) => row.primary === "yes");
      if (existingPrimary) {
        setError(
          `This account already has a primary contact (${existingPrimary.name || "Unknown"}). ` +
            "Please remove the primary status from that contact first before setting this contact as primary."
        );
        return;
      }
      if (!isPrimaryContactComplete(editedContact)) {
        setError(getPrimaryContactRequiredFieldsMessage());
        return;
      }
      setEditedContact(applyPrimaryCommunicationPrefs({ ...editedContact, primary: "yes" }));
      setPendingNewPrimary(null);
      setError(null);
      return;
    }

    if (editedContact.primary !== "yes") {
      return;
    }

    if (otherContacts.length === 0) {
      setError("Cannot remove primary status as this is the only contact for this account");
      return;
    }

    setShowSelectPrimaryDialog(true);
  };

  const handleSelectNewPrimary = (selectedContact) => {
    if (!isPrimaryContactComplete(selectedContact)) {
      setError(getPrimaryContactIncompleteMessage(selectedContact?.name));
      return;
    }
    setPendingNewPrimary(selectedContact);
    setEditedContact({ ...editedContact, primary: "no" });
    setShowSelectPrimaryDialog(false);
    setError(null);
  };

  const handleSave = async () => {
    if (editedContact.primary === "yes" && !isPrimaryContactComplete(editedContact)) {
      setError(getPrimaryContactRequiredFieldsMessage());
      return;
    }
    if (pendingNewPrimary && !isPrimaryContactComplete(pendingNewPrimary)) {
      setError(getPrimaryContactIncompleteMessage(pendingNewPrimary?.name));
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onSave({
        ...editedContact,
        pendingNewPrimaryContactId: pendingNewPrimary?.customer_contact_id || null,
      });
    } catch (err) {
      setError(err?.message || "Failed to update contact.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Contact</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Name"
              value={editedContact.name || ""}
              onChange={handleChange("name")}
              fullWidth
            />
            <TextField
              label="Telephone"
              value={editedContact.telephone || ""}
              onChange={handleChange("telephone")}
              fullWidth
            />
            <TextField
              label="WhatsApp"
              value={editedContact.whatsapp || ""}
              onChange={handleChange("whatsapp")}
              fullWidth
            />
            <TextField
              label="Email"
              value={editedContact.email || ""}
              onChange={handleChange("email")}
              fullWidth
            />
            {error ? (
              <MDTypography color="error" variant="caption" display="block" sx={{ mt: -0.5 }}>
                {error}
              </MDTypography>
            ) : null}
            <FormControlLabel
              control={
                <Checkbox checked={editedContact.primary === "yes"} onChange={handlePrimaryChange} />
              }
              label="Primary Contact"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedContact.get_email === "yes"}
                  onChange={handleNotificationChange("get_email")}
                />
              }
              label="Receive Email"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedContact.get_whatsapp === "yes"}
                  onChange={handleNotificationChange("get_whatsapp")}
                />
              }
              label="Receive WhatsApp"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedContact.get_billing === "yes"}
                  onChange={handleNotificationChange("get_billing")}
                />
              }
              label="Receive Billing"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={editedContact.get_notification === "yes"}
                  onChange={handleNotificationChange("get_notification")}
                />
              }
              label="Receive Notifications"
            />
            {pendingNewPrimary ? (
              <MDTypography variant="caption" color="text">
                New primary contact: <strong>{pendingNewPrimary.name || "Selected contact"}</strong>
              </MDTypography>
            ) : null}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={onClose} disabled={saving}>
            Cancel
          </MDButton>
          <MDButton onClick={handleSave} variant="gradient" color="info" disabled={saving}>
            {saving ? "Saving..." : "Save Changes"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <SelectPrimaryContactDialog
        open={showSelectPrimaryDialog}
        onClose={() => setShowSelectPrimaryDialog(false)}
        onSelect={handleSelectNewPrimary}
        contacts={otherContacts}
        currentContactId={Number(editedContact?.customer_contact_id || 0)}
      />
    </>
  );
}

EditContactDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  contact: PropTypes.shape({
    customer_contact_id: PropTypes.number.isRequired,
    name: PropTypes.string,
    telephone: PropTypes.string,
    whatsapp: PropTypes.string,
    email: PropTypes.string,
    primary: PropTypes.string,
    get_email: PropTypes.string,
    get_whatsapp: PropTypes.string,
    get_billing: PropTypes.string,
    get_notification: PropTypes.string,
  }).isRequired,
  onSave: PropTypes.func.isRequired,
  allContacts: PropTypes.arrayOf(
    PropTypes.shape({
      customer_contact_id: PropTypes.number.isRequired,
      name: PropTypes.string,
      telephone: PropTypes.string,
      whatsapp: PropTypes.string,
      email: PropTypes.string,
      primary: PropTypes.string,
      get_email: PropTypes.string,
      get_whatsapp: PropTypes.string,
      get_billing: PropTypes.string,
      get_notification: PropTypes.string,
    })
  ).isRequired,
};

function SelectPrimaryContactDialog({ open, onClose, onSelect, contacts, currentContactId }) {
  const [selectedContact, setSelectedContact] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) {
      setSelectedContact(null);
      setError(null);
    }
  }, [open]);

  const handleSelect = () => {
    if (!selectedContact) {
      setError("Please select a contact");
      return;
    }
    if (!isPrimaryContactComplete(selectedContact)) {
      setError(getPrimaryContactIncompleteMessage(selectedContact?.name));
      return;
    }
    onSelect(selectedContact);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Select New Primary Contact</DialogTitle>
      <DialogContent>
        <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
          <FormControl fullWidth>
            <InputLabel>Select Contact</InputLabel>
            <Select
              value={selectedContact || ""}
              onChange={(e) => setSelectedContact(e.target.value)}
              label="Select Contact"
            >
              {contacts.map((contact) => (
                <MenuItem 
                  key={contact.customer_contact_id} 
                  value={contact}
                  disabled={contact.customer_contact_id === currentContactId}
                >
                  {contact.name} ({contact.email || contact.telephone})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {error && (
            <MDTypography color="error" variant="caption">
              {error}
            </MDTypography>
          )}
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton onClick={handleSelect} variant="gradient" color="info">
          Select
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

SelectPrimaryContactDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  contacts: PropTypes.arrayOf(PropTypes.shape({
    customer_contact_id: PropTypes.number.isRequired,
    name: PropTypes.string,
    email: PropTypes.string,
    telephone: PropTypes.string,
    whatsapp: PropTypes.string,
  })).isRequired,
  currentContactId: PropTypes.number.isRequired
};

function EditAccountDialog({ open, onClose, onSave, account, contactId, contactDetails }) {
  const [notificationPrefs, setNotificationPrefs] = useState({
    primary_contact: "no",
    get_email: "no",
    get_whatsapp: "no",
    get_billing: "no",
    get_notification: "no"
  });
  const [error, setError] = useState(null);
  const [validating, setValidating] = useState(false);
  const [canSetPrimary, setCanSetPrimary] = useState(false);
  const [primaryContactInfo, setPrimaryContactInfo] = useState(null);
  const [showSelectPrimaryDialog, setShowSelectPrimaryDialog] = useState(false);
  const [availableContacts, setAvailableContacts] = useState([]);

  // Update notification preferences when account changes
  useEffect(() => {
    if (account) {
      setNotificationPrefs({
        primary_contact: account.primary_contact || "no",
        get_email: account.get_email || "no",
        get_whatsapp: account.get_whatsapp || "no",
        get_billing: account.get_billing || "no",
        get_notification: account.get_notification || "no"
      });
    }
  }, [account]);

  const validateNotificationRequirements = async (newPrefs) => {
    try {
      setValidating(true);
      // Fetch all contacts linked to this customer
      const response = await api.post("/db/customer_contact_links/filter", {
        filters: {
          customer_id: account.customer_id
        }
      });

      // Filter out the current contact being edited
      const otherLinks = response.filter(
        link => link.customer_contact_link_id !== account.customer_contact_link_id
      );

      // Check notification requirements
      const notificationFields = ["get_email", "get_whatsapp", "get_billing", "get_notification"];
      for (const field of notificationFields) {
        const hasOtherNotification = otherLinks.some(link => link[field]?.toLowerCase() === 'yes');
        if (newPrefs[field] === "no" && !hasOtherNotification) {
          return `This account must have at least one contact receiving ${field.replace("get_", "")}`;
        }
      }

      return null;
    } catch (err) {
      console.error("Error validating requirements:", err);
      return "Error validating requirements";
    } finally {
      setValidating(false);
    }
  };

  useEffect(() => {
    const validatePrimaryContact = async () => {
      if (!open || !account) return;
      
      try {
        setValidating(true);
        setError(null);
        
        // Fetch all contacts linked to this account
        const response = await api.post("/db/customer_contact_links/filter", {
          filters: {
            customer_id: account.customer_id
          },
          columns: [
            "customer_contact_id",
            "primary_contact",
            "customer_contact_link_id"
          ]
        });

        // Filter out the current contact being edited
        const otherLinks = response.filter(
          link => link.customer_contact_link_id !== account.customer_contact_link_id
        );

        // Check if any other contact is primary
        const primaryContact = otherLinks.find(link => link.primary_contact?.toLowerCase() === 'yes');
        
        if (primaryContact) {
          // Fetch the primary contact's details
          const contactResponse = await api.post("/db/customer_contacts/filter", {
            filters: {
              customer_contact_id: primaryContact.customer_contact_id
            },
            columns: ["name", "email", "telephone"]
          });

          if (contactResponse && contactResponse.length > 0) {
            setPrimaryContactInfo(contactResponse[0]);
          }
        }
        
        setCanSetPrimary(!primaryContact);
      } catch (err) {
        console.error("Error validating primary contact:", err);
        setError("Failed to validate primary contact status");
        setCanSetPrimary(false);
      } finally {
        setValidating(false);
      }
    };

    validatePrimaryContact();
  }, [open, account, notificationPrefs.primary_contact]);

  const handlePrimaryChange = async (event) => {
    const newValue = event.target.checked ? "yes" : "no";
    
    // If trying to set primary but not allowed
    if (newValue === "yes" && !canSetPrimary) {
      setError(
        `This account already has a primary contact (${primaryContactInfo?.name || 'Unknown'}). ` +
        `Please remove the primary status from that contact first before setting this contact as primary.`
      );
      return;
    }

    if (newValue === "yes") {
      if (!isPrimaryContactComplete(contactDetails)) {
        setError(getPrimaryContactRequiredFieldsMessage());
        return;
      }
      const newPrefs = applyPrimaryCommunicationPrefs({
        ...notificationPrefs,
        primary_contact: "yes",
      });
      setNotificationPrefs(newPrefs);
      setError(null);
      return;
    }

    // If trying to unset primary
    if (newValue === "no" && notificationPrefs.primary_contact === "yes") {
      try {
        // Fetch all contacts linked to this account
        const response = await api.post("/db/customer_contact_links/filter", {
          filters: {
            customer_id: account.customer_id
          }
        });

        // Get all contacts except the current one
        const otherContacts = response.filter(
          link => link.customer_contact_link_id !== account.customer_contact_link_id
        );

        if (otherContacts.length === 0) {
          setError("Cannot remove primary status as this is the only contact for this account");
          return;
        }

        // Fetch contact details for the other contacts
        const contactIds = otherContacts.map(link => link.customer_contact_id);
        const contactsResponse = await api.post("/db/customer_contacts/filter", {
          filters: {
            customer_contact_id: contactIds
          },
          columns: ["customer_contact_id", "name", "email", "telephone", "whatsapp"]
        });

        setAvailableContacts(contactsResponse);
        setShowSelectPrimaryDialog(true);
        return;
      } catch (err) {
        console.error("Error fetching contacts:", err);
        setError("Failed to fetch available contacts");
        return;
      }
    }

    const newPrefs = { ...notificationPrefs, primary_contact: newValue };
    setNotificationPrefs(newPrefs);
    setError(null);
  };

  const handleSelectNewPrimary = async (selectedContact) => {
    try {
      if (!isPrimaryContactComplete(selectedContact)) {
        setError(getPrimaryContactIncompleteMessage(selectedContact?.name));
        return;
      }

      // First, find the link ID for the selected contact
      const linksResponse = await api.post("/db/customer_contact_links/filter", {
        filters: {
          customer_id: account.customer_id,
          customer_contact_id: selectedContact.customer_contact_id
        }
      });

      if (!linksResponse || linksResponse.length === 0) {
        setError("Failed to find contact link");
        return;
      }

      const linkId = linksResponse[0].customer_contact_link_id;

      // Update the selected contact to be primary with all communication prefs enabled
      await api.put(`/db/customer_contact_links/${linkId}`, {
        primary_contact: "yes",
        get_email: "yes",
        get_whatsapp: "yes",
        get_billing: "yes",
        get_notification: "yes",
      });

      // Update current contact to not be primary
      const newPrefs = { ...notificationPrefs, primary_contact: "no" };
      setNotificationPrefs(newPrefs);
      setError(null);
    } catch (err) {
      console.error("Error updating primary contact:", err);
      setError(err?.message || "Failed to update primary contact");
    }
  };

  const handleNotificationChange = (field) => async (event) => {
    const newValue = event.target.checked ? "yes" : "no";
    const newPrefs = { ...notificationPrefs, [field]: newValue };

    // Only validate when unchecking a box
    if (newValue === "no") {
      const validationError = await validateNotificationRequirements(newPrefs);
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setNotificationPrefs(newPrefs);
    setError(null);
  };

  const handleSave = async () => {
    try {
      // Validate primary contact status
      if (notificationPrefs.primary_contact === "yes" && !canSetPrimary) {
        setError("This account already has a primary contact. Please remove the primary status from the other contact first.");
        return;
      }

      if (notificationPrefs.primary_contact === "yes" && !isPrimaryContactComplete(contactDetails)) {
        setError(getPrimaryContactRequiredFieldsMessage());
        return;
      }

      const prefsToSave =
        notificationPrefs.primary_contact === "yes"
          ? applyPrimaryCommunicationPrefs({
              ...notificationPrefs,
              primary: "yes",
              primary_contact: "yes",
            })
          : notificationPrefs;

      // Validate other notification requirements
      const validationError = await validateNotificationRequirements(prefsToSave);
      if (validationError) {
        setError(validationError);
        return;
      }

      // Update the current contact's notification preferences
      await api.put(`/db/customer_contact_links/${account.customer_contact_link_id}`, {
        primary_contact: prefsToSave.primary_contact,
        get_email: prefsToSave.get_email,
        get_whatsapp: prefsToSave.get_whatsapp,
        get_billing: prefsToSave.get_billing,
        get_notification: prefsToSave.get_notification
      });

      onSave(prefsToSave);
      onClose();
    } catch (err) {
      setError(err?.message || "Failed to update account link");
      console.error("Error updating account link:", err);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Account Link</DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" gap={2} mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notificationPrefs.primary_contact === "yes"}
                  onChange={handlePrimaryChange}
                  disabled={validating}
                />
              }
              label="Primary Contact"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={notificationPrefs.get_email === "yes"}
                  onChange={handleNotificationChange("get_email")}
                  disabled={validating}
                />
              }
              label="Receive Email"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={notificationPrefs.get_whatsapp === "yes"}
                  onChange={handleNotificationChange("get_whatsapp")}
                  disabled={validating}
                />
              }
              label="Receive WhatsApp"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={notificationPrefs.get_billing === "yes"}
                  onChange={handleNotificationChange("get_billing")}
                  disabled={validating}
                />
              }
              label="Receive Billing"
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={notificationPrefs.get_notification === "yes"}
                  onChange={handleNotificationChange("get_notification")}
                  disabled={validating}
                />
              }
              label="Receive Notifications"
            />

            {error && (
              <MDTypography color="error" variant="caption">
                {error}
              </MDTypography>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={onClose} disabled={validating}>Cancel</MDButton>
          <MDButton 
            onClick={handleSave} 
            variant="gradient" 
            color="info" 
            disabled={validating}
          >
            {validating ? "Validating..." : "Save"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <SelectPrimaryContactDialog
        open={showSelectPrimaryDialog}
        onClose={() => setShowSelectPrimaryDialog(false)}
        onSelect={handleSelectNewPrimary}
        contacts={availableContacts}
        currentContactId={contactId}
      />
    </>
  );
}

EditAccountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  account: PropTypes.shape({
    customer_contact_link_id: PropTypes.number.isRequired,
    customer_id: PropTypes.number.isRequired,
    primary_contact: PropTypes.string.isRequired,
    get_email: PropTypes.string.isRequired,
    get_whatsapp: PropTypes.string.isRequired,
    get_billing: PropTypes.string.isRequired,
    get_notification: PropTypes.string.isRequired
  }).isRequired,
  contactId: PropTypes.number.isRequired,
  contactDetails: PropTypes.shape({
    name: PropTypes.string,
    telephone: PropTypes.string,
    whatsapp: PropTypes.string,
    email: PropTypes.string,
  }),
};

function DeleteAccountDialog({ open, onClose, onConfirm, account }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Account Link</DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <MDTypography>
            Are you sure you want to remove this account link? This action cannot be undone.
          </MDTypography>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose}>Cancel</MDButton>
        <MDButton onClick={onConfirm} variant="gradient" color="error">
          Delete
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

DeleteAccountDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  account: PropTypes.shape({
    customer_contact_link_id: PropTypes.number.isRequired,
    account: PropTypes.string.isRequired
  }).isRequired
};

function ContactView({
  portalVariant = "admin",
  expectedCustomerId = null,
  clientAccessToken = null,
}) {
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("md"));
  const { contactId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isClientPortal = portalVariant === "client";
  const wrapContactViewShell = (content) => {
    if (isClientPortal) {
      return content;
    }

    return (
      <DashboardLayout>
        <DashboardNavbar />
        {content}
      </DashboardLayout>
    );
  };
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState(null);
  const [customerContacts, setCustomerContacts] = useState([]);
  const [accountCustomerId, setAccountCustomerId] = useState(null);
  const [error, setError] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [loadingLinkedAccounts, setLoadingLinkedAccounts] = useState(true);
  const [showAddAccountDialog, setShowAddAccountDialog] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(null);

  const returnUrl = location.state?.returnUrl || (isClientPortal ? "/profile" : "/customers/contacts");
  const returnState = isClientPortal
    ? { activeCustomerTab: location.state?.activeCustomerTab ?? 1 }
    : undefined;

  useEffect(() => {
    loadData();
  }, [contactId, isClientPortal, clientAccessToken, expectedCustomerId]);

  useEffect(() => {
    if (isClientPortal) {
      setLinkedAccounts([]);
      setLoadingLinkedAccounts(false);
      return;
    }

    const fetchLinkedAccounts = async () => {
      try {
        setLoadingLinkedAccounts(true);
        // First get the contact links
        const linksResponse = await api.post("/db/customer_contact_links/filter", {
          filters: {
            customer_contact_id: parseInt(contactId)
          }
        });

        if (linksResponse && linksResponse.length > 0) {
          // Get customer details for each link
          const customerIds = linksResponse.map(link => link.customer_id);
          const customersResponse = await api.post("/db/customers/filter", {
            filters: {
              customer_id: customerIds
            }
          });

          // Combine the data
          const accounts = linksResponse.map(link => {
            const customer = customersResponse.find(c => c.customer_id === link.customer_id);
            return {
              customer_contact_link_id: link.customer_contact_link_id,
              customer_id: link.customer_id,
              account: customer ? `${customer.code} - ${customer.name}` : 'Unknown Account',
              primary_contact: link.primary_contact,
              get_email: link.get_email,
              get_whatsapp: link.get_whatsapp,
              get_billing: link.get_billing,
              get_notification: link.get_notification
            };
          });

          setLinkedAccounts(accounts);
        }
      } catch (error) {
        console.error("Error fetching linked accounts:", error);
      } finally {
        setLoadingLinkedAccounts(false);
      }
    };

    if (contactId) {
      fetchLinkedAccounts();
    }
  }, [contactId, isClientPortal]);

  const handleBack = () => {
    navigate(returnUrl, returnState ? { state: returnState } : undefined);
  };

  const loadCustomerContacts = async (customerId) => {
    const parsedCustomerId = Number(customerId);
    if (!Number.isFinite(parsedCustomerId) || parsedCustomerId < 1) {
      setCustomerContacts([]);
      return [];
    }

    if (isClientPortal) {
      if (!clientAccessToken) {
        setCustomerContacts([]);
        return [];
      }
      const contacts = await fetchClientzoneContacts(clientAccessToken);
      setCustomerContacts(contacts);
      return contacts;
    }

    const contacts = await fetchCustomerMergedContacts(parsedCustomerId);
    setCustomerContacts(contacts);
    return contacts;
  };

  const promoteContactPrimary = async (customerId, targetContactId) => {
    if (!targetContactId) return;

    if (isClientPortal) {
      if (!clientAccessToken) {
        throw new Error("Session expired. Please sign in again.");
      }
      await updateClientzoneContact(
        targetContactId,
        { primary: "yes" },
        clientAccessToken
      );
      return;
    }

    const contactsResponse = await api.post("/db/customer_contacts/filter", {
      filters: {
        customer_contact_id: Number(targetContactId),
      },
      columns: ["customer_contact_id", "name", "telephone", "whatsapp", "email"],
    });
    const targetContact = contactsResponse?.[0];
    if (!isPrimaryContactComplete(targetContact)) {
      throw new Error(getPrimaryContactIncompleteMessage(targetContact?.name));
    }

    const linksResponse = await api.post("/db/customer_contact_links/filter", {
      filters: {
        customer_id: Number(customerId),
        customer_contact_id: Number(targetContactId),
      },
    });
    if (!linksResponse?.length) {
      throw new Error("Failed to find contact link");
    }
    await api.put(`/db/customer_contact_links/${linksResponse[0].customer_contact_link_id}`, {
      primary_contact: "yes",
      get_email: "yes",
      get_whatsapp: "yes",
      get_billing: "yes",
      get_notification: "yes",
    });
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (isClientPortal) {
        if (!clientAccessToken || !expectedCustomerId) {
          setError("Session expired. Please sign in again.");
          return;
        }

        const contactData = await fetchClientzoneContact(contactId, clientAccessToken);
        if (!contactData) {
          setError("Contact not found");
          return;
        }
        if (Number(contactData.customer_contact_id || 0) < 1) {
          setError("Contact not found");
          return;
        }
        setContact(contactData);
        setAccountCustomerId(Number(expectedCustomerId));
        await loadCustomerContacts(expectedCustomerId);
        return;
      }

      // Fetch contact data
      const response = await api.post("/db/customer_contacts/filter", {
        filters: {
          customer_contact_id: parseInt(contactId),
        },
      });

      if (!response || response.length === 0) {
        setError("Contact not found");
        return;
      }

      // Fetch contact links to get notification preferences
      const linksResponse = await api.post("/db/customer_contact_links/filter", {
        filters: {
          customer_contact_id: parseInt(contactId),
        },
      });

      const contactData = {
        ...response[0],
        primary: linksResponse[0]?.primary_contact || "no",
        get_email: linksResponse[0]?.get_email || "no",
        get_whatsapp: linksResponse[0]?.get_whatsapp || "no",
        get_billing: linksResponse[0]?.get_billing || "no",
        get_notification: linksResponse[0]?.get_notification || "no",
      };

      setContact(contactData);
      const customerId = linksResponse?.[0]?.customer_id;
      setAccountCustomerId(customerId ? Number(customerId) : null);
      if (customerId) {
        await loadCustomerContacts(customerId);
      } else {
        setCustomerContacts([contactData]);
      }
    } catch (err) {
      setError("Failed to load contact data");
      console.error("Error loading contact:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditContact = () => {
    setEditingContact(contact);
  };

  const handleSaveContact = async (updatedContact) => {
    try {
      const pendingNewPrimaryContactId = Number(updatedContact.pendingNewPrimaryContactId || 0);
      const customerIdForPrimary = isClientPortal
        ? Number(expectedCustomerId)
        : Number(accountCustomerId || 0);

      if (pendingNewPrimaryContactId > 0 && !isClientPortal) {
        if (!customerIdForPrimary) {
          throw new Error("Unable to determine customer account for primary contact update.");
        }
        await promoteContactPrimary(customerIdForPrimary, pendingNewPrimaryContactId);
      }

      if (isClientPortal) {
        if (!clientAccessToken) {
          throw new Error("Session expired. Please sign in again.");
        }

        await updateClientzoneContact(
          updatedContact.customer_contact_id,
          {
            name: updatedContact.name,
            telephone: updatedContact.telephone,
            whatsapp: updatedContact.whatsapp,
            email: updatedContact.email,
            primary: updatedContact.primary,
            get_email: updatedContact.get_email,
            get_whatsapp: updatedContact.get_whatsapp,
            get_billing: updatedContact.get_billing,
            get_notification: updatedContact.get_notification,
            pending_new_primary_contact_id: pendingNewPrimaryContactId || undefined,
          },
          clientAccessToken
        );

        await loadData();
        setEditingContact(null);
        notifySuccess("Contact updated successfully", { useHotToast: isClientPortal });
        return;
      }

      // First update the main contact
      await api.put(`/db/customer_contacts/${updatedContact.customer_contact_id}`, {
        name: updatedContact.name,
        telephone: updatedContact.telephone,
        whatsapp: updatedContact.whatsapp,
        email: updatedContact.email,
      });

      // Update the link with notification preferences and primary status
      const linksResponse = await api.post("/db/customer_contact_links/filter", {
        filters: {
          customer_contact_id: updatedContact.customer_contact_id,
          ...(accountCustomerId ? { customer_id: accountCustomerId } : {}),
        },
      });

      if (linksResponse && linksResponse.length > 0) {
        const currentLink = linksResponse[0];
        await api.put(`/db/customer_contact_links/${currentLink.customer_contact_link_id}`, {
          primary_contact: updatedContact.primary,
          get_email: updatedContact.get_email,
          get_whatsapp: updatedContact.get_whatsapp,
          get_billing: updatedContact.get_billing,
          get_notification: updatedContact.get_notification,
        });
      }

      // Refresh data
      await loadData();
      setEditingContact(null);
      notifySuccess("Contact updated successfully", { useHotToast: isClientPortal });
    } catch (error) {
      console.error("Error updating contact:", error);
      notifyError(error?.message || "Failed to update contact", { useHotToast: isClientPortal });
      throw error;
    }
  };

  const handleAddAccount = () => {
    setShowAddAccountDialog(true);
  };

  const handleSaveAccount = async () => {
    try {
      setLoadingLinkedAccounts(true);
      // Refresh the linked accounts data
      const linksResponse = await api.post("/db/customer_contact_links/filter", {
        filters: {
          customer_contact_id: parseInt(contactId)
        }
      });

      if (linksResponse && linksResponse.length > 0) {
        const customerIds = linksResponse.map(link => link.customer_id);
        const customersResponse = await api.post("/db/customers/filter", {
          filters: {
            customer_id: customerIds
          }
        });

        const accounts = linksResponse.map(link => {
          const customer = customersResponse.find(c => c.customer_id === link.customer_id);
          return {
            customer_contact_link_id: link.customer_contact_link_id,
            customer_id: link.customer_id,
            account: customer ? `${customer.code} - ${customer.name}` : 'Unknown Account',
            primary_contact: link.primary_contact,
            get_email: link.get_email,
            get_whatsapp: link.get_whatsapp,
            get_billing: link.get_billing,
            get_notification: link.get_notification
          };
        });

        setLinkedAccounts(accounts);
      }
    } catch (error) {
      console.error("Error refreshing linked accounts:", error);
    } finally {
      setLoadingLinkedAccounts(false);
    }
  };

  const handleDeleteAccount = async () => {
    try {
      if (!deletingAccount) return;

      await api.delete(`/db/customer_contact_links/${deletingAccount.customer_contact_link_id}`);
      await handleSaveAccount(); // Refresh the list
      setDeletingAccount(null);
    } catch (err) {
      console.error("Error deleting account link:", err);
    }
  };

  const handleViewCustomer = (customerId) => {
    navigate(`/customers/view/${customerId}`, {
      state: { returnUrl: `/customers/contacts/${contactId}` }
    });
  };

  const linkedAccountsTable = useMemo(
    () => ({
      columns: [
        { Header: "Account", accessor: "account", width: "30%" },
        {
          Header: "Actions",
          accessor: "actions",
          width: "15%",
          Cell: ({ row }) => (
            <MDBox display="flex" gap={0.25}>
              <MDButton
                variant="text"
                color="info"
                onClick={() => handleViewCustomer(row.original.customer_id)}
                sx={{ minWidth: "auto", px: 0.5 }}
              >
                <VisibilityIcon />
              </MDButton>
              <MDButton
                variant="text"
                color="info"
                onClick={() => setEditingAccount(row.original)}
                sx={{ minWidth: "auto", px: 0.5 }}
              >
                <Icon>edit</Icon>
              </MDButton>
              <MDButton
                variant="text"
                color="error"
                onClick={() => setDeletingAccount(row.original)}
                sx={{ minWidth: "auto", px: 0.5 }}
              >
                <Icon>delete</Icon>
              </MDButton>
            </MDBox>
          ),
        },
        {
          Header: "Primary Contact",
          accessor: "primary_contact",
          width: "15%",
          Cell: ({ value }) => (value === "yes" ? "Yes" : "No"),
        },
        {
          Header: "Email",
          accessor: "get_email",
          width: "15%",
          Cell: ({ value }) => (value === "yes" ? "Yes" : "No"),
        },
        {
          Header: "WhatsApp",
          accessor: "get_whatsapp",
          width: "15%",
          Cell: ({ value }) => (value === "yes" ? "Yes" : "No"),
        },
        {
          Header: "Billing",
          accessor: "get_billing",
          width: "15%",
          Cell: ({ value }) => (value === "yes" ? "Yes" : "No"),
        },
        {
          Header: "Notifications",
          accessor: "get_notification",
          width: "15%",
          Cell: ({ value }) => (value === "yes" ? "Yes" : "No"),
        },
      ],
      rows: linkedAccounts,
    }),
    [linkedAccounts]
  );

  const contactDetailFields = useMemo(() => {
    if (!contact) return [];
    return [
      { label: "Name", value: contact.name || "Not provided" },
      {
        label: "Telephone",
        value: contact.telephone
          ? formatPhoneNumberForDisplay(contact.telephone)
          : "Not provided",
      },
      {
        label: "WhatsApp",
        value: contact.whatsapp
          ? formatPhoneNumberForDisplay(contact.whatsapp)
          : "Not provided",
      },
      { label: "Email", value: contact.email || "Not provided" },
      { label: "Primary Contact", value: contact.primary === "yes" ? "Yes" : "No" },
      { label: "Receive Email", value: contact.get_email === "yes" ? "Yes" : "No" },
      { label: "Receive WhatsApp", value: contact.get_whatsapp === "yes" ? "Yes" : "No" },
      { label: "Receive Billing", value: contact.get_billing === "yes" ? "Yes" : "No" },
      { label: "Receive Notifications", value: contact.get_notification === "yes" ? "Yes" : "No" },
    ];
  }, [contact]);

  if (loading) {
    return wrapContactViewShell(
      <MDBox p={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  if (error) {
    return wrapContactViewShell(
      <MDBox p={3}>
        <MDTypography color="error">{error}</MDTypography>
        <MDButton onClick={handleBack} sx={{ mt: 2 }}>
          Go Back
        </MDButton>
      </MDBox>
    );
  }

  return wrapContactViewShell(
      <MDBox py={{ xs: 2, md: 3 }} pt={{ xs: 3, md: 3 }} px={{ xs: 1, md: 0 }}>
        <MDBox mb={3} mt={{ xs: 0.5, md: 0 }}>
          <Card>
            <MDBox
              mx={2}
              mt={{ xs: 1, md: -3 }}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={{ xs: 1.5, md: 0 }}
            >
              <MDBox display="flex" flexDirection="column" alignItems={{ xs: "flex-start", md: "center" }} gap={1}>
                <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <MDButton
                    variant="text"
                    color="white"
                    onClick={handleBack}
                    sx={{ minWidth: "auto", px: 1 }}
                  >
                    <Icon>arrow_back</Icon>&nbsp;Back
                  </MDButton>
                  <MDTypography variant="h6" color="white">
                    Contact Information
                  </MDTypography>
                </MDBox>
                <MDButton
                  variant="outlined"
                  color="white"
                  onClick={handleEditContact}
                  sx={{
                    alignSelf: { xs: "stretch", md: "flex-start" },
                    display: { xs: "flex", md: "none" },
                  }}
                >
                  <Icon>edit</Icon>&nbsp;Edit Contact
                </MDButton>
              </MDBox>
              <MDButton
                variant="outlined"
                color="white"
                onClick={handleEditContact}
                sx={{ display: { xs: "none", md: "inline-flex" }, flexShrink: 0 }}
              >
                <Icon>edit</Icon>&nbsp;Edit Contact
              </MDButton>
            </MDBox>
            <CardContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
              <MDBox display="flex" flexDirection="column" gap={2}>
                {contactDetailFields.map((field) => (
                  <MDBox key={field.label} sx={contactFieldLabelSx}>
                    <MDTypography variant="body2" color="text" fontWeight="medium" sx={{ flexShrink: 0 }}>
                      {field.label}:
                    </MDTypography>
                    <MDTypography variant="body2" color="text" sx={{ wordBreak: "break-word" }}>
                      {field.value}
                    </MDTypography>
                  </MDBox>
                ))}
              </MDBox>
            </CardContent>
          </Card>
        </MDBox>

        {!isClientPortal && (
        <MDBox mb={3} mt={{ xs: 3, md: 6 }}>
          <Card>
            <MDBox
              mx={2}
              mt={{ xs: 1, md: -3 }}
              py={3}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
              display="flex"
              flexDirection={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
              gap={{ xs: 1.5, md: 0 }}
            >
              <MDTypography variant="h6" color="white">
                Linked Accounts
              </MDTypography>
              <MDButton
                variant="outlined"
                color="white"
                onClick={handleAddAccount}
                sx={{ alignSelf: { xs: "stretch", md: "auto" } }}
              >
                <Icon>add</Icon>&nbsp;Add New
              </MDButton>
            </MDBox>
            <CardContent sx={{ px: { xs: 2, md: 3 }, py: { xs: 2, md: 3 } }}>
              {isMobileView ? (
                <ContactLinkedAccountsMobileCards
                  accounts={linkedAccounts}
                  loading={loadingLinkedAccounts}
                  onViewCustomer={handleViewCustomer}
                  onEditAccount={setEditingAccount}
                  onDeleteAccount={setDeletingAccount}
                />
              ) : loadingLinkedAccounts ? (
                <MDBox display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </MDBox>
              ) : (
                <DataTable
                  table={linkedAccountsTable}
                  isSorted={false}
                  entriesPerPage={false}
                  showTotalEntries={false}
                  noEndBorder
                />
              )}
            </CardContent>
          </Card>
        </MDBox>
        )}

        {editingContact && (
          <EditContactDialog
            open={!!editingContact}
            onClose={() => setEditingContact(null)}
            contact={editingContact}
            onSave={handleSaveContact}
            allContacts={customerContacts.length ? customerContacts : [contact]}
          />
        )}

        {showAddAccountDialog && !isClientPortal && (
          <AddCustomerContactLinkDialog
            open={showAddAccountDialog}
            onClose={() => setShowAddAccountDialog(false)}
            onSave={handleSaveAccount}
            contactId={parseInt(contactId)}
            existingAccounts={linkedAccounts}
          />
        )}

        {editingAccount && !isClientPortal && (
          <EditAccountDialog
            open={!!editingAccount}
            onClose={() => setEditingAccount(null)}
            onSave={handleSaveAccount}
            account={editingAccount}
            contactId={parseInt(contactId)}
            contactDetails={contact}
          />
        )}

        {deletingAccount && !isClientPortal && (
          <DeleteAccountDialog
            open={!!deletingAccount}
            onClose={() => setDeletingAccount(null)}
            onConfirm={handleDeleteAccount}
            account={deletingAccount}
          />
        )}
      </MDBox>
  );
}

ContactView.propTypes = {
  portalVariant: PropTypes.oneOf(["admin", "client"]),
  expectedCustomerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  clientAccessToken: PropTypes.string,
};

export default ContactView; 